import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import GlobalLoader from '@/components/context/GlobalLoader';
import { useNavigate } from 'react-router-dom';
import { User, Receipt, Shield, CreditCard, ExternalLink, Bell } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { API_BASE } from '../lib/apiConfig';
import '@styles/pages/account.css';

type Profile = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone_e164: string;
  sms_consent_at: string | null;
  sms_opt_out_at: string | null;
  // email_opt_out_at, symmetric to sms_opt_out_at. Null = receiving email
  // (the default for everyone, since email is the implicit primary
  // channel). Stamped to a timestamptz when the user toggles email off
  // here. Email send paths gate on (email_opt_out_at IS NULL).
  email_opt_out_at: string | null;
};

type BillingItem = {
  id: string;
  label: string;
  amount: number | null;
  date: string;
};

type CurrentPlan = {
  plan: string;
  billsUsed: number;
  billsLimit: number;
  startDate: string;
};

/* Plan slug to the name a customer knows it by. */
function planLabel(plan: string | null | undefined): string {
  switch (plan) {
    case 'annual': return 'Annual Membership';
    case 'eob': return 'EOB Review';
    case 'action_plan': return 'Personalized Action Plan';
    default: return 'Pay Per Bill';
  }
}

export default function Account() {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [currentPlan, setCurrentPlan] = useState<CurrentPlan | null>(null);

  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState(''); // display: MM/DD/YYYY
  const [profileSaving, setProfileSaving] = useState(false);

  // Communication preferences, derived from profile.sms_consent_at /
  // sms_opt_out_at. Eligible to receive SMS iff consent_at is set AND
  // opt_out_at is null. The toggle reflects this derived eligibility.
  const smsOptedIn = !!(profile?.sms_consent_at && !profile?.sms_opt_out_at);
  const [smsSaving, setSmsSaving] = useState(false);

  // Email is the implicit-consent channel (transactional, CAN-SPAM
  // exempt) so the default state is opted-in. We only store the
  // OPT-OUT timestamp; null means receiving email. UI-side that's
  // inverted vs SMS, this flag is "is email currently ON?".
  const emailOptedIn = !profile?.email_opt_out_at;
  const [emailSaving, setEmailSaving] = useState(false);

  // Account closure. Fully manual: the button just emails the team a
  // request — no automated deletion. requested flips to true once the
  // request lands so the button can't be spammed and the user gets
  // clear feedback that it's in.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeRequested, setCloseRequested] = useState(false);

  /* DOB helpers, mirrors Onboarding logic */
  const handleDobChange = (raw: string) => {
    let digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length === 0) { setDob(''); return; }

    if (digits.length >= 1) {
      const m1 = Number(digits[0]);
      if (m1 > 1) digits = '0' + digits[0] + digits.slice(1);
    }
    if (digits.length >= 2) {
      let mm = Number(digits.slice(0, 2));
      if (mm === 0) mm = 1;
      if (mm > 12) mm = 12;
      digits = String(mm).padStart(2, '0') + digits.slice(2);
    }
    if (digits.length >= 4) {
      const mm = Number(digits.slice(0, 2));
      let dd = Number(digits.slice(2, 4));
      const maxDay = mm === 2 ? 29 : [4, 6, 9, 11].includes(mm) ? 30 : 31;
      if (dd === 0) dd = 1;
      if (dd > maxDay) dd = maxDay;
      digits = digits.slice(0, 2) + String(dd).padStart(2, '0') + digits.slice(4);
    } else if (digits.length === 3) {
      const d1 = Number(digits[2]);
      if (d1 > 3) digits = digits.slice(0, 2) + '0' + digits[2] + digits.slice(3);
    }
    if (digits.length >= 8) {
      let yyyy = Number(digits.slice(4, 8));
      const thisYear = new Date().getFullYear();
      if (yyyy < 1900) yyyy = 1900;
      if (yyyy > thisYear) yyyy = thisYear;
      digits = digits.slice(0, 4) + String(yyyy);
    }
    digits = digits.slice(0, 8);

    let formatted = '';
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      formatted = digits;
    }
    setDob(formatted);
  };

  const parseDob = (display: string): string | null => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const [, mm, dd, yyyy] = match;
    const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    if (isNaN(date.getTime())) return null;
    if (date.getMonth() + 1 !== Number(mm) || date.getDate() !== Number(dd)) return null;
    return `${yyyy}-${mm}-${dd}`;
  };

  const isAdult = (isoDate: string) => {
    const birth = new Date(isoDate);
    if (isNaN(birth.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 18;
  };

  /** Convert ISO yyyy-mm-dd → MM/DD/YYYY for the input */
  const isoToDisplay = (iso: string): string => {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    return `${match[2]}/${match[3]}/${match[1]}`;
  };


  useEffect(() => {
    if (authLoading || !user) return;

    const load = async () => {
      // Fetch profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('first_name, last_name, date_of_birth, phone_e164, sms_consent_at, sms_opt_out_at, email_opt_out_at')
        .eq('id', user.id)
        .single();

      if (profileData) {
        setProfile(profileData);
        setFirstName(profileData.first_name || '');
        setLastName(profileData.last_name || '');
        setPhone(profileData.phone_e164 || '');
        setDob(profileData.date_of_birth ? isoToDisplay(profileData.date_of_birth) : '');
      }

      // Build billing history: always start with the initial subscription,
      // then layer on any addon_payments (additional bills, upgrades).
      const items: BillingItem[] = [];

      // 1. Fetch the initial subscription (the first purchase the user ever made)
      const { data: subData } = await supabase
        .from('subscriptions')
        .select('id, plan, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (subData) {
        const initialAmount =
          subData.plan === 'annual' ? 17900
          : subData.plan === 'action_plan' ? 4900
          : subData.plan === 'eob' ? 6900
          : 5900;
        items.push({
          id: `sub-${subData.id}`,
          label: planLabel(subData.plan),
          amount: initialAmount,
          date: subData.created_at,
        });
      }

      // 2. Fetch addon_payments (additional bills, upgrades, etc.)
      const { data: paymentData } = await supabase
        .from('addon_payments')
        .select('id, amount, plan, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (paymentData && paymentData.length > 0) {
        for (const p of paymentData) {
          let label = 'Payment';
          switch (p.plan) {
            case 'initial_annual':
              label = 'Annual Membership';
              break;
            case 'initial_one_off':
              label = 'Pay Per Bill';
              break;
            case 'one_off':
            case 'annual':
              label = 'Additional Bill';
              break;
            case 'action_plan':
              label = 'Personalized Action Plan, another bill';
              break;
            case 'upgrade_to_annual':
              label = 'Upgrade to Annual';
              break;
            default:
              label = p.plan || 'Payment';
          }
          items.push({
            id: p.id,
            label,
            amount: p.amount,
            date: p.created_at,
          });
        }
      }

      // Sort all items by date, newest first
      items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setBillingItems(items);

      // Fetch current plan
      const { data: subData2 } = await supabase
        .from('subscriptions')
        .select('plan, bills_used, bills_limit, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subData2) {
        setCurrentPlan({
          plan: planLabel(subData2.plan),
          billsUsed: subData2.bills_used ?? 0,
          billsLimit: subData2.bills_limit ?? 1,
          startDate: subData2.created_at,
        });
      }

      setLoading(false);
    };

    load();
  }, [authLoading, user]);

  /* Save profile changes */
  const handleSaveProfile = async () => {
    if (!user) return;

    // Validation
    if (!firstName.trim()) {
      showToast('First name is required.', 'error');
      return;
    }
    if (!lastName.trim()) {
      showToast('Last name is required.', 'error');
      return;
    }
    if (firstName.trim().length < 2) {
      showToast('First name must be at least 2 characters.', 'error');
      return;
    }
    if (lastName.trim().length < 2) {
      showToast('Last name must be at least 2 characters.', 'error');
      return;
    }
    if (phone.trim() && !/^\+?[\d\s\-().]{7,20}$/.test(phone.trim())) {
      showToast('Please enter a valid phone number.', 'error');
      return;
    }

    // DOB validation
    let isoDob: string | undefined;
    if (dob.trim()) {
      const parsed = parseDob(dob);
      if (!parsed) {
        showToast('Please enter a valid date of birth (MM/DD/YYYY).', 'error');
        return;
      }
      if (!isAdult(parsed)) {
        showToast('You must be at least 18 years old.', 'error');
        return;
      }
      isoDob = parsed;
    }

    setProfileSaving(true);

    // Phone-change re-consent: if the user is changing the phone number
    // attached to the account AND they currently have SMS consent on
    // file, the consent is tied to the OLD number, not the new one.
    // TCPA requires re-opting in for a new number. We clear consent
    // here, and the user is prompted to re-toggle SMS in the section
    // below. We compare digits-only so cosmetic edits ("(415) 555-0100"
    // → "+1 415 555 0100") don't trigger spurious re-consent.
    const digitsOnly = (s: string) => s.replace(/\D/g, '');
    const oldPhoneDigits = digitsOnly(profile?.phone_e164 || '');
    const newPhoneDigits = digitsOnly(phone);
    const phoneChanged =
      !!oldPhoneDigits && !!newPhoneDigits && oldPhoneDigits !== newPhoneDigits;
    const hadSmsConsent = !!(profile?.sms_consent_at && !profile?.sms_opt_out_at);
    const shouldRevokeSmsConsent = phoneChanged && hadSmsConsent;

    const updatePayload: Record<string, string | null> = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone_e164: phone.trim(),
    };
    if (isoDob) {
      updatePayload.date_of_birth = isoDob;
    }
    if (shouldRevokeSmsConsent) {
      // Wipe consent, the new number hasn't been opted in. Stamp
      // opt_out_at so the eligibility check returns false until the
      // user re-toggles. Keep sms_consent_at non-null so the audit
      // trail shows there WAS prior consent for the old number, but
      // bump it to null since it's no longer valid.
      updatePayload.sms_consent_at = null;
      updatePayload.sms_opt_out_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('profiles')
      .update(updatePayload)
      .eq('id', user.id);

    if (error) {
      showToast('Failed to update profile.', 'error');
    } else {
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              phone_e164: phone.trim(),
              ...(isoDob ? { date_of_birth: isoDob } : {}),
              ...(shouldRevokeSmsConsent
                ? {
                    sms_consent_at: null,
                    sms_opt_out_at: new Date().toISOString(),
                  }
                : {}),
            }
          : prev
      );
      setEditingProfile(false);
      if (shouldRevokeSmsConsent) {
        showToast(
          'Profile updated. Confirm text alerts on your new number below.',
          'success'
        );
      } else {
        showToast('Profile updated.', 'success');
      }
    }

    setProfileSaving(false);
  };

  /* SMS preference toggle.
     Turning ON: stamps sms_consent_at + sms_consent_user_agent and clears
     any prior sms_opt_out_at (re-opt-in resets the opt-out).
     Turning OFF: stamps sms_opt_out_at. Doesn't clear sms_consent_at  
     we preserve the original consent record for audit. Eligibility check
     gates on (consent_at IS NOT NULL AND opt_out_at IS NULL). */
  const handleToggleSms = async (nextOptedIn: boolean) => {
    if (!user || smsSaving) return;
    setSmsSaving(true);

    const nowIso = new Date().toISOString();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    const patch: Record<string, unknown> = nextOptedIn
      ? {
          sms_consent_at: nowIso,
          sms_consent_user_agent: userAgent,
          sms_opt_out_at: null,
          // Stamp prompt_seen_at so the existing-customer modal won't
          // re-prompt them, they've explicitly chosen here.
          sms_consent_prompt_seen_at: nowIso,
        }
      : {
          sms_opt_out_at: nowIso,
          sms_consent_prompt_seen_at: nowIso,
        };

    const { error } = await supabase.from('profiles').update(patch).eq('id', user.id);

    if (error) {
      showToast('Could not save preference. Please try again.', 'error');
    } else {
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              sms_consent_at: nextOptedIn ? nowIso : prev.sms_consent_at,
              sms_opt_out_at: nextOptedIn ? null : nowIso,
            }
          : prev
      );
      showToast(
        nextOptedIn ? 'Text alerts turned on.' : 'Text alerts turned off.',
        'success'
      );
    }

    setSmsSaving(false);
  };

  /* Email preference toggle.
     Email is implicit-consent (CAN-SPAM transactional), so the toggle
     stores only an opt-out timestamp. Turning OFF stamps email_opt_out_at;
     turning ON clears it back to null. Send paths gate on
     (email_opt_out_at IS NULL). We don't have an email_consent_at
     because consent is implicit at signup and there's no auditable
     "moment", the symmetric SMS column exists because SMS consent is
     express, not implicit. */
  const handleToggleEmail = async (nextOptedIn: boolean) => {
    if (!user || emailSaving) return;
    setEmailSaving(true);

    const patch = nextOptedIn
      ? { email_opt_out_at: null }
      : { email_opt_out_at: new Date().toISOString() };

    const { error } = await supabase.from('profiles').update(patch).eq('id', user.id);

    if (error) {
      showToast('Could not save preference. Please try again.', 'error');
    } else {
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              email_opt_out_at: nextOptedIn ? null : new Date().toISOString(),
            }
          : prev
      );
      showToast(
        nextOptedIn ? 'Email updates turned on.' : 'Email updates turned off.',
        'success'
      );
    }

    setEmailSaving(false);
  };

  /* Request account closure. Sends an authenticated request to the server,
     which emails the team (manual handling) and confirms to the user. We
     intentionally do NOT delete or change anything client-side. */
  const handleRequestClose = async () => {
    if (!user || closeSubmitting) return;
    setCloseSubmitting(true);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;

      const res = await fetch(`${API_BASE}/account/request-deletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showToast(body.error || 'Could not submit your request. Please try again.', 'error');
        return;
      }

      setShowCloseConfirm(false);
      setCloseRequested(true);
      showToast('Request received. We\'ll email you to confirm shortly.', 'success');
    } catch {
      showToast('Could not submit your request. Please try again.', 'error');
    } finally {
      setCloseSubmitting(false);
    }
  };


  if (loading) return <GlobalLoader message="Loading your account…" />;

  return (
      <main className="account-content">
        <h1 className="account-heading">Account</h1>

        {/* Profile Section */}
        <section className="account-section">
          <div className="account-section-header">
            <User size={18} />
            <h2>Profile</h2>
          </div>

          {!editingProfile ? (
            <div className="account-info-grid">
              <div className="account-info-row">
                <span className="account-info-label">Name</span>
                <span className="account-info-value">
                  {profile?.first_name} {profile?.last_name}
                </span>
              </div>
              <div className="account-info-row">
                <span className="account-info-label">Email</span>
                <span className="account-info-value">{user?.email}</span>
              </div>
              <div className="account-info-row">
                <span className="account-info-label">Phone</span>
                <span className="account-info-value">
                  {profile?.phone_e164 || ' '}
                </span>
              </div>
              <div className="account-info-row">
                <span className="account-info-label">Date of Birth</span>
                <span className="account-info-value">
                  {profile?.date_of_birth
                    ? new Date(profile.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : ' '}
                </span>
              </div>
              <button
                className="account-edit-btn"
                onClick={() => setEditingProfile(true)}
              >
                Edit Profile
              </button>
            </div>
          ) : (
            <div className="account-edit-form">
              <div className="account-form-row">
                <label>First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="account-form-row">
                <label>Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="account-form-row">
                <label>Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="account-form-row">
                <label>Date of Birth</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="MM/DD/YYYY"
                  value={dob}
                  onChange={(e) => handleDobChange(e.target.value)}
                />
              </div>
              <div className="account-form-actions">
                <button
                  className="account-cancel-btn"
                  onClick={() => {
                    setEditingProfile(false);
                    setFirstName(profile?.first_name || '');
                    setLastName(profile?.last_name || '');
                    setPhone(profile?.phone_e164 || '');
                    setDob(profile?.date_of_birth ? isoToDisplay(profile.date_of_birth) : '');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="account-save-btn"
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                >
                  {profileSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Communication Preferences */}
        <section className="account-section">
          <div className="account-section-header">
            <Bell size={18} />
            <h2>Communication Preferences</h2>
          </div>

          {/* Email channel, implicit consent at signup, can be toggled
              off here. Symmetric layout to the SMS row below. */}
          <div className="account-pref-row">
            <div className="account-pref-text">
              <div className="account-pref-title">Email updates</div>
              <div className="account-pref-sub">
                {emailOptedIn
                  ? 'On. Get an email when there\'s an update on your case.'
                  : 'Off. You won\'t receive case-update emails.'}
              </div>
              {!emailOptedIn && profile?.email_opt_out_at && (
                <div className="account-pref-fine">
                  Opted out {new Date(profile.email_opt_out_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  {' '}&middot; Account &amp; security emails will still be sent.
                </div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={emailOptedIn}
              aria-label="Toggle email updates"
              className={`account-pref-toggle ${emailOptedIn ? 'is-on' : ''}`}
              onClick={() => handleToggleEmail(!emailOptedIn)}
              disabled={emailSaving}
            >
              <span className="account-pref-toggle-thumb" />
            </button>
          </div>

          {/* SMS channel, express consent required. Toggle is disabled
              if we don't have a phone on file. */}
          <div className="account-pref-row">
            <div className="account-pref-text">
              <div className="account-pref-title">Text message updates</div>
              <div className="account-pref-sub">
                {smsOptedIn
                  ? 'On. Get a text when there\'s an update on your case.'
                  : 'Off. Get a text when there\'s an update on your case.'}
              </div>
              {!smsOptedIn && (
                <div className="account-pref-fine">
                  Automated SMS &middot; Msg &amp; data rates may apply &middot; Reply <strong>STOP</strong> to opt out
                </div>
              )}
              {smsOptedIn && profile?.sms_consent_at && (
                <div className="account-pref-fine">
                  Opted in {new Date(profile.sms_consent_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  {' '}&middot; Reply <strong>STOP</strong> to any text to opt out instantly.
                </div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={smsOptedIn}
              aria-label="Toggle text message updates"
              className={`account-pref-toggle ${smsOptedIn ? 'is-on' : ''}`}
              onClick={() => handleToggleSms(!smsOptedIn)}
              disabled={smsSaving || !profile?.phone_e164}
              title={!profile?.phone_e164 ? 'Add a phone number to enable text alerts' : undefined}
            >
              <span className="account-pref-toggle-thumb" />
            </button>
          </div>

          {!profile?.phone_e164 && (
            <p className="account-pref-empty">
              Add a phone number above to enable text alerts.
            </p>
          )}

          {/* Soft warning if both channels are off, we still need a way
              to reach the user. They can keep both off if they want, but
              we tell them what to expect. */}
          {!emailOptedIn && !smsOptedIn && (
            <p className="account-pref-empty">
              Both channels are off. We won't send case updates, check the
              dashboard regularly to stay on top of your bills.
            </p>
          )}
        </section>

        {/* Current Plan */}
        {currentPlan && (
          <section className="account-section">
            <div className="account-section-header">
              <CreditCard size={18} />
              <h2>Your Plan</h2>
            </div>
            <div className="account-plan-card">
              <div className="account-plan-name">{currentPlan.plan}</div>
              <div className="account-plan-meta">
                {currentPlan.billsUsed} of {currentPlan.billsLimit} bills used
              </div>
              <div className="account-plan-meta">
                Member since {new Date(currentPlan.startDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
            </div>
            <button
              className="account-edit-btn"
              onClick={() => navigate('/plan')}
              style={{ marginTop: 16 }}
            >
              Manage Plan
              <ExternalLink size={13} style={{ marginLeft: 6, opacity: 0.6 }} />
            </button>
          </section>
        )}

        {/* Payment History */}
        <section className="account-section">
          <div className="account-section-header">
            <Receipt size={18} />
            <h2>Payment History</h2>
          </div>
          {billingItems.length === 0 ? (
            <p className="account-empty">No payments yet.</p>
          ) : (
            <div className="account-payments-list">
              {billingItems.map((item) => (
                <div key={item.id} className="account-payment-row">
                  <div className="account-payment-info">
                    <span className="account-payment-label">
                      {item.label}
                    </span>
                    <span className="account-payment-date">
                      {new Date(item.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <span className="account-payment-amount">
                    {item.amount != null
                      ? `$${(item.amount / 100).toFixed(2)}`
                      : ' '}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Security Section */}
        <section className="account-section">
          <div className="account-section-header">
            <Shield size={18} />
            <h2>Security</h2>
          </div>
          <div className="account-info-grid">
            <div className="account-info-row">
              <span className="account-info-label">Sign-in method</span>
              <span className="account-info-value">Email (magic link)</span>
            </div>
            <div className="account-info-row">
              <span className="account-info-label">Last sign-in</span>
              <span className="account-info-value">
                {user?.last_sign_in_at
                  ? new Date(user.last_sign_in_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : ' '}
              </span>
            </div>
          </div>

          {/* Account closure. Handled manually by the team — this just
              sends them a request and confirms by email. */}
          <div className="account-close-row">
            <div className="account-close-text">
              <div className="account-close-title">Close your account</div>
              <div className="account-close-sub">
                {closeRequested
                  ? 'Request received. We\'ll email you to confirm once it\'s done, usually within 1 to 2 business days.'
                  : 'Request to close your account and have your data removed. A member of our team handles this personally and confirms by email.'}
              </div>
            </div>
            <button
              type="button"
              className="account-close-btn"
              onClick={() => setShowCloseConfirm(true)}
              disabled={closeSubmitting || closeRequested}
            >
              {closeRequested ? 'Request sent' : 'Close account'}
            </button>
          </div>
        </section>

        <ConfirmDialog
          open={showCloseConfirm}
          title="Close your account?"
          message="We'll send your request to our team, who will close your account and remove your data. You'll get an email confirming the request, and another once it's complete. If you have an active subscription, let us know in your reply and we'll handle the billing too."
          confirmLabel={closeSubmitting ? 'Sending…' : 'Yes, send request'}
          cancelLabel="Keep my account"
          variant="danger"
          onConfirm={handleRequestClose}
          onCancel={() => setShowCloseConfirm(false)}
        />

      </main>
  );
}
